import React, { useEffect, useState } from 'react';

import { MDText } from 'i18n-react';

import { Content } from 'LegacySBCRA/Content/Content';

import DialogLoader from '../../../../../components/transitions/DialogLoader/DialogLoader';
import { RoundedSection } from '../../../common/RoundedSection/RoundedSection';
import { BookMOT } from '../../Components/BookMOT/BookMOT';
import { CheckerContentBlocks } from '../../Components/CheckerContentBlocks/CheckerContentBlocks';
import { CheckerHero } from '../../Components/CheckerHero/CheckerHero';
import { CheckerInfoSection } from '../../Components/CheckerInfoSection/CheckerInfoSection';
import { CheckerUsp } from '../../Components/CheckerUsp/CheckerUsp';
import { ExpandableContentWrapper } from '../../Components/ExpandableContentWrapper/ExpandableContentWrapper';
import { GuidesSection as Guides } from '../../Components/GuidesSection/GuidesSection';
import { RelatedGuidesSection } from '../../Components/RelatedGuidesSection/RelatedGuidesSection';
import { ToolsSection } from '../../Components/ToolsSection/ToolsSection';
import { useCheckerToolsAnalytics } from '../../Hooks/useCheckerToolsAnalytics';
import Texts from '../../texts.json';
import { useCheckerContext } from '../CheckerContext';
import { CHECKER_TOOLS_PAGE_SECTIONS, createFAQData } from '../CheckerRouter.utils';

import { getContentSectionData, getRelatedGuidesContent } from './CheckerPage.helpers';

import styles from './CheckerPage.scss';

const LocalT = new MDText(Texts);

export type CheckerPageType = 'motCheck' | 'taxCheck';

type CheckerPageProp = {
  checkerPage: CheckerPageType;
}

const CheckerPage = ({ checkerPage }: CheckerPageProp) => {
  const { acf, apiError } = useCheckerContext();
  const { triggerClickEvent: triggerAnalyticsClickEvent, triggerSectionLoadEvent } = useCheckerToolsAnalytics();

  const [loading, setLoading] = useState(!acf);

  useEffect(() => {
    if (acf || apiError) {
      setLoading(false);
    }
  }, [acf, apiError]);

  if (loading) {
    return (<DialogLoader />);
  }

  const { faq_title: faqTitle, related_guide_header: relatedGuidesTitle } = acf || {};

  const isAcfContentUnavailable = apiError && !acf?.content;
  const seoContentSectionData = getContentSectionData(acf);

  const faq = createFAQData(acf);
  const isFAQEmpty = (!Array.isArray(faq) || !faq.length);

  const relatedGuides = getRelatedGuidesContent(acf);
  const isRelatedGuidesEmpty = (!Array.isArray(relatedGuides) || !relatedGuides.length);

  const isMotCheck = checkerPage === 'motCheck';

  return (
    <section>
      <CheckerHero checkerPage={checkerPage} description={acf?.header_description} title={acf?.header_title} />
      <CheckerUsp
        {...{ isMotCheck }}
        onLoad={() => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.USP)}
      />
      {isAcfContentUnavailable
        ? (<Content className={styles.textCenter}>
          <p>{LocalT.translate('content.notAvailable')}</p>
        </Content>)
        : (<>
          <RoundedSection withBottomLedge withTopLedge theme="off-white">
            {isMotCheck && <BookMOT onLoad={() => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.KWIK_FIT)} />}
            <CheckerContentBlocks
              contentData={seoContentSectionData.slice(0, 3)}
              onLoad={(label: string) => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.SEO, label)}
              withoutTopPadding={isMotCheck}
            />
          </RoundedSection>
          <RoundedSection withBottomLedge withTopLedge>
            <CheckerInfoSection
              {...{ acf, isMotCheck }}
              onLoad={() => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.CHARGES)}
            />
          </RoundedSection>
          <RoundedSection withBottomLedge withTopLedge theme="off-white">
            <ToolsSection
              analyticsEvents={{
                onLoad: () => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.TOOLS_TILE),
                onTileClick: ({ label, url }: { label: string; url: string }) =>
                  triggerAnalyticsClickEvent({ label, name: CHECKER_TOOLS_PAGE_SECTIONS.TOOLS_TILE, url }),
              }}
              videoSectionContent={seoContentSectionData[3]}
            />
          </RoundedSection>
          {!isFAQEmpty && (
            <RoundedSection withBottomLedge withTopLedge>
              <ExpandableContentWrapper
                {...{ faq, faqTitle }}
                analyticsEvents={{
                  onButtonClick: ({ isOpen, label }: { isOpen: boolean; label: string }) =>
                    triggerAnalyticsClickEvent({ label, name: `${CHECKER_TOOLS_PAGE_SECTIONS.FAQ}_topic_${isOpen ? 'open' : 'close'}` }),
                  onLoad: () => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.FAQ),
                }}
              />
            </RoundedSection>
          )}
          {!isRelatedGuidesEmpty && (
            <RoundedSection withBottomLedge withTopLedge theme="off-white">
              <RelatedGuidesSection
                analyticsEvents={{
                  onButtonClick: ({ label, url }: { label: string; url: string }) =>
                    triggerAnalyticsClickEvent({ label, name: CHECKER_TOOLS_PAGE_SECTIONS.MOT_GUIDE, url }),
                  onLoad: () => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.MOT_GUIDE),
                }}
                guides={relatedGuides}
                sectionTitle={relatedGuidesTitle}
              />
            </RoundedSection>
          )}
          <Guides
            analyticsEvents={{
              onGuideClick: ({ label, url }: { label: string; url: string }) =>
                triggerAnalyticsClickEvent({ label, name: CHECKER_TOOLS_PAGE_SECTIONS.GUIDE, url }),
              onLoad: (label: string) => triggerSectionLoadEvent(CHECKER_TOOLS_PAGE_SECTIONS.GUIDE, label),
            }}
          />
        </>)
      }
    </section>
  );
};

export default CheckerPage;
